import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const values = [
  {
    icon: "ri-quill-pen-line",
    title: "先把故事讲清楚",
    desc: "剧情简介不剧透关键反转，但会交代清楚人物、动机与背景，让你判断这部作品是否值得看。",
  },
  {
    icon: "ri-compass-3-line",
    title: "推荐要有理由",
    desc: "每一条推荐都来自编辑实际观看与讨论，不做无差别堆砌，也不使用夸张标题吸引点击。",
  },
  {
    icon: "ri-shield-check-line",
    title: "尊重版权与创作",
    desc: "本站只提供影视介绍与资讯内容，不提供播放、下载与任何形式的资源存储服务。",
  },
];

const team = [
  { role: "内容主编", name: "林深", desc: "十年影视媒体经验，负责选题与终审" },
  { role: "资深编辑", name: "沈知意", desc: "关注行业动态与产业链变化" },
  { role: "特约撰稿", name: "何砚", desc: "负责主创访谈与幕后报道" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 姐姐的朋友",
    description:
      "了解姐姐的朋友：一个专注影视剧情介绍与影视资讯的内容平台。我们坚持把故事讲清楚，提供客观的影视推荐与行业观察。",
    keywords: "关于我们,姐姐的朋友,影视内容平台,影视资讯,剧情介绍",
  });

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />

      <main>
        <section className="relative overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Artistic%20abstract%20photograph%20of%20an%20empty%20vintage%20cinema%20hall%20with%20deep%20navy%20blue%20seats%20and%20warm%20amber%20light%20beams%20cutting%20through%20dust%20from%20a%20projector%2C%20film%20grain%20texture%2C%20minimal%20symmetrical%20composition%2C%20cinematic%20atmosphere%2C%20high%20detail%20editorial%20photography&width=1600&height=700&seq=about-hero-cinema&orientation=landscape"
              alt="影院放映厅光影"
              className="h-full w-full object-cover object-top opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/50" />
          </div>

          <div className="relative mx-auto w-full max-w-[1400px] px-4 pb-12 pt-5 md:px-6 md:pb-16 md:pt-6">
            <div className="[&_a]:text-primary-300 [&_a:hover]:text-accent-400 [&_span]:text-primary-400">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            </div>

            <div className="mt-10 max-w-3xl">
              <p className="font-label text-[11px] tracking-[0.3em] text-accent-400">ABOUT US</p>
              <h1 className="mt-3 text-3xl font-black leading-tight text-background-50 md:text-5xl">
                关于姐姐的朋友
              </h1>
              <p className="mt-5 text-sm leading-8 text-background-100/90 md:text-base md:leading-9">
                我们是一个专注影视内容介绍与资讯的小团队。这里的每一部作品都有完整的剧情说明、演职员信息与真实评价，
                帮助你在打开播放器之前，先弄清楚它到底讲了什么。
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4">
                {[
                  { value: "1,200+", label: "影视条目" },
                  { value: "3,600+", label: "剧情解析" },
                  { value: "480+", label: "深度资讯" },
                  { value: "6 年", label: "持续更新" },
                ].map((stat) => (
                  <div key={stat.label}>
                    <p className="font-label text-2xl font-extrabold text-accent-400 md:text-3xl">{stat.value}</p>
                    <p className="mt-1 text-xs text-primary-300">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-7">
              <p className="font-label text-[11px] tracking-[0.26em] text-foreground-400">OUR STORY</p>
              <h2 className="mt-2 flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                网站简介
              </h2>
              <div className="mt-4 space-y-4 text-sm leading-8 text-foreground-600">
                <p>
                  「姐姐的朋友」最初只是一份几个人共享的看片笔记。我们习惯在看完一部作品后，把剧情、人物和那些说不清的感受写下来，
                  发给身边的朋友。后来笔记越来越多，就有了这个网站。
                </p>
                <p>
                  今天的网站包含影视分类、影视详情与影视资讯三个主要板块。你可以按类型、地区、年份筛选作品，
                  在详情页查看完整剧情简介与演职员信息，也可以在资讯频道了解行业动态、新片幕后与主创访谈。
                </p>
                <p>
                  我们没有做播放功能，也不打算做。我们相信，介绍和推荐本身就是一种独立的服务——
                  它帮你在有限的时间里，找到真正想看的那一部。
                </p>
              </div>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h3 className="text-base font-bold text-foreground-950">运营理念</h3>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                  {values.map((value) => (
                    <div key={value.title}>
                      <span className="grid h-10 w-10 place-items-center rounded-md bg-accent-100 text-accent-800">
                        <i className={`${value.icon} text-lg`} />
                      </span>
                      <h4 className="mt-3 text-sm font-semibold text-foreground-900">{value.title}</h4>
                      <p className="mt-1.5 text-xs leading-6 text-foreground-500">{value.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="aspect-[4/3] w-full overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20warm%20minimal%20editorial%20studio%20desk%20with%20stacks%20of%20film%20scripts%2C%20a%20notebook%20with%20handwritten%20notes%2C%20a%20vintage%20film%20camera%20and%20a%20cup%20of%20coffee%2C%20soft%20window%20light%2C%20deep%20navy%20and%20amber%20tones%2C%20film%20grain%2C%20high%20detail%20magazine%20photography&width=800&height=600&seq=about-studio-desk&orientation=landscape"
                    alt="编辑部工作桌面与剧本笔记"
                    title="姐姐的朋友编辑部 - 影视内容工作室"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="text-base font-bold text-foreground-950">内容团队</h3>
                  <ul className="mt-4 space-y-4">
                    {team.map((member) => (
                      <li key={member.role} className="flex gap-3">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary-100 text-primary-700">
                          <i className="ri-user-line text-base" />
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">
                            {member.name}
                            <span className="ml-2 text-[11px] font-normal text-foreground-500">{member.role}</span>
                          </p>
                          <p className="mt-0.5 text-xs text-foreground-500">{member.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <section className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:col-span-7 md:p-8">
              <p className="font-label text-[11px] tracking-[0.26em] text-foreground-400">CONTACT</p>
              <h2 className="mt-2 flex items-center gap-2 text-xl font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-primary-500" />
                联系我们
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                内容纠错、合作洽谈、投稿或版权问题，都欢迎通过邮件联系。我们通常会在两个工作日内回复。
              </p>
              <div className="mt-5 space-y-3 text-sm">
                <a
                  href="mailto:hello@jiejiedepengyou.com?subject=%E6%9D%A5%E8%87%AA%E5%A7%90%E5%A7%90%E7%9A%84%E6%9C%8B%E5%8F%8B%E7%BD%91%E7%AB%99%E7%9A%84%E5%92%A8%E8%AF%A2"
                  className="flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-4 py-3 text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-mail-send-line text-base" />
                  hello@jiejiedepengyou.com
                </a>
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <i className="ri-mail-open-line text-base text-foreground-400" />
                  点击邮箱地址将自动调起系统邮件客户端
                </div>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-3 text-xs text-foreground-600 sm:grid-cols-3">
                <div className="rounded-md bg-background-100 px-3 py-3">
                  <p className="text-foreground-400">内容纠错</p>
                  <p className="mt-1 text-foreground-800">标注文章链接即可</p>
                </div>
                <div className="rounded-md bg-background-100 px-3 py-3">
                  <p className="text-foreground-400">投稿合作</p>
                  <p className="mt-1 text-foreground-800">请附作品与选题</p>
                </div>
                <div className="rounded-md bg-background-100 px-3 py-3">
                  <p className="text-foreground-400">版权事宜</p>
                  <p className="mt-1 text-foreground-800">请提供权属证明</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-secondary-100 p-6 md:col-span-5 md:p-8">
              <p className="font-label text-[11px] tracking-[0.26em] text-secondary-700">LEGAL</p>
              <h2 className="mt-2 text-xl font-bold text-foreground-950">版权与法律声明</h2>
              <div className="mt-3 space-y-3 text-xs leading-6 text-secondary-900">
                <p>
                  本站所有影视作品的海报、剧照与文字简介仅用于内容介绍与评论，版权归原权利人所有。
                  如相关权利人认为内容使用不当，可通过上方邮箱联系我们，我们将在核实后及时处理。
                </p>
                <p>
                  本站不提供任何影视作品的在线播放、下载或存储服务，站内所有资源链接均不指向影音文件。
                  用户在本站的收藏记录仅保存在本地浏览器，不会上传至服务器。
                </p>
                <p>© 2025 姐姐的朋友 · 未经授权，禁止转载本站原创内容。</p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to="/category"
                  className="cursor-pointer rounded-md bg-background-50 px-3 py-2 text-xs text-secondary-900 transition-colors hover:bg-accent-100"
                >
                  浏览影视分类
                </Link>
                <Link
                  to="/news"
                  className="cursor-pointer rounded-md bg-background-50 px-3 py-2 text-xs text-secondary-900 transition-colors hover:bg-accent-100"
                >
                  查看影视资讯
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}